import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS, getImageUrl } from '../config/api';

const CowDetailPage = ({ cowData, onBack, activeBottomTab, setActiveBottomTab }) => {
  const [loading, setLoading] = useState(true);
  const [produkDetail, setProdukDetail] = useState(null);
  const [timbanganList, setTimbanganList] = useState([]);
  const [vaksinList, setVaksinList] = useState([]);
  const [produkImages, setProdukImages] = useState([]);

  // Format currency helper
  const formatCurrency = (value) => {
    if (!value) return 'Rp 0';
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(numValue);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 
                     'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Format bulan tahun helper
  const formatBulanTahun = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 
                     'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return month;
    } catch (error) {
      return dateString;
    }
  };

  // Fetch data dari API
  useEffect(() => {
    const fetchCowDetailData = async () => {
      if (!cowData) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Get produkId dari cowData
        const produkId = cowData.id || cowData.rawProdukData?.id || null;
        
        if (!produkId) {
          setLoading(false);
          return;
        }

        // Fetch produk detail
        try {
          const produkResponse = await apiClient.get(API_ENDPOINTS.PRODUK.DETAIL(produkId));
          let produk = null;
          if (produkResponse.data && produkResponse.data.produk) {
            produk = produkResponse.data.produk;
          } else if (produkResponse.data && produkResponse.data.data) {
            produk = produkResponse.data.data;
          } else if (produkResponse.data && !produkResponse.data.produk) {
            produk = produkResponse.data;
          } else if (!produkResponse.data) {
            produk = produkResponse;
          }
          setProdukDetail(produk);

          // Fetch produk images
          try {
            const imagesResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_IMAGES(produkId));
            let images = [];
            if (imagesResponse.data && Array.isArray(imagesResponse.data)) {
              images = imagesResponse.data;
            } else if (imagesResponse.data && imagesResponse.data.images && Array.isArray(imagesResponse.data.images)) {
              images = imagesResponse.data.images;
            }
            setProdukImages(images);
          } catch (error) {
            console.error('Error fetching produk images:', error);
          }
        } catch (error) {
          console.error('Error fetching produk detail:', error);
        }

        // Fetch timbangan produk
        try {
          const timbanganResponse = await apiClient.get(API_ENDPOINTS.TIMBANGAN_PRODUK.BY_PRODUK(produkId));
          let timbangan = [];
          if (timbanganResponse.data && Array.isArray(timbanganResponse.data)) {
            timbangan = timbanganResponse.data;
          } else if (timbanganResponse.data && timbanganResponse.data.timbangan_produk && Array.isArray(timbanganResponse.data.timbangan_produk)) {
            timbangan = timbanganResponse.data.timbangan_produk;
          } else if (timbanganResponse.data && timbanganResponse.data.data && Array.isArray(timbanganResponse.data.data)) {
            timbangan = timbanganResponse.data.data;
          } else if (timbanganResponse.data && timbanganResponse.data.timbangan_produks && Array.isArray(timbanganResponse.data.timbangan_produks)) {
            timbangan = timbanganResponse.data.timbangan_produks;
          }
          // Urutkan berdasarkan tanggal (terbaru dulu)
          timbangan.sort((a, b) => {
            const dateA = new Date(a.created_at || a.tanggal || 0);
            const dateB = new Date(b.created_at || b.tanggal || 0);
            return dateB - dateA;
          });
          setTimbanganList(timbangan);
        } catch (error) {
          console.error('Error fetching timbangan produk:', error);
          setTimbanganList([]);
        }

        // Fetch vaksin produk
        try {
          const vaksinResponse = await apiClient.get(API_ENDPOINTS.VAKSIN_PRODUK.BY_PRODUK(produkId));
          let vaksin = [];
          if (vaksinResponse.data && Array.isArray(vaksinResponse.data)) {
            vaksin = vaksinResponse.data;
          } else if (vaksinResponse.data && vaksinResponse.data.vaksin_produk && Array.isArray(vaksinResponse.data.vaksin_produk)) {
            vaksin = vaksinResponse.data.vaksin_produk;
          } else if (vaksinResponse.data && vaksinResponse.data.data && Array.isArray(vaksinResponse.data.data)) {
            vaksin = vaksinResponse.data.data;
          } else if (vaksinResponse.data && vaksinResponse.data.vaksin_produks && Array.isArray(vaksinResponse.data.vaksin_produks)) {
            vaksin = vaksinResponse.data.vaksin_produks;
          }
          // Urutkan berdasarkan tanggal (terbaru dulu)
          vaksin.sort((a, b) => {
            const dateA = new Date(a.created_at || a.tanggal || 0);
            const dateB = new Date(b.created_at || b.tanggal || 0);
            return dateB - dateA;
          });
          setVaksinList(vaksin);
        } catch (error) {
          console.error('Error fetching vaksin produk:', error);
          setVaksinList([]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching cow detail data:', error);
        setLoading(false);
      }
    };

    fetchCowDetailData();
  }, [cowData]);

  // Get image URL helper
  const getProdukImageUrl = () => {
    if (produkImages && Array.isArray(produkImages) && produkImages.length > 0) {
      const firstImage = produkImages[0];
      if (typeof firstImage === 'string') {
        return getImageUrl(firstImage);
      } else if (firstImage.url) {
        return getImageUrl(firstImage.url);
      } else if (firstImage.filename) {
        const produkId = cowData?.id || produkDetail?.id || '';
        return getImageUrl(`/images/produk/${produkId}/${firstImage.filename}`);
      }
    }
    return cowData?.image || '/sapi1.jpg';
  };

  // Prepare weight data untuk grafik dari timbanganList
  const prepareWeightDataForChart = () => {
    if (!timbanganList || timbanganList.length === 0) {
      return { weightDataForChart: [], yAxisLabels: [325, 320, 315, 310, 305, 300, 295, 290, 285, 280, 275], yAxisMin: 275, yAxisMax: 325, yAxisRange: 50 };
    }
    
    // Ambil maksimal 12 data terakhir, urutkan dari lama ke baru untuk chart
    const sortedTimbangan = [...timbanganList].reverse().slice(0, 12);
    const weightDataForChart = sortedTimbangan.map((timbangan) => {
      const bobot = timbangan.bobot || timbangan.weight || 0;
      const bobotNumber = typeof bobot === 'number' ? bobot : parseFloat(bobot) || 0;
      const tanggal = timbangan.created_at || timbangan.tanggal || '';
      const bulan = tanggal ? formatBulanTahun(tanggal) : `Data`;
      return {
        month: bulan,
        weight: bobotNumber,
        tanggal: tanggal
      };
    });

    // Hitung min dan max untuk Y-axis
    const weights = weightDataForChart.map(d => d.weight);
    const minWeight = weights.length > 0 ? Math.min(...weights) : 275;
    const maxWeight = weights.length > 0 ? Math.max(...weights) : 325;
    const yAxisMin = Math.max(0, Math.floor((minWeight - 10) / 5) * 5);
    const yAxisMax = Math.ceil((maxWeight + 10) / 5) * 5;
    const yAxisRange = yAxisMax - yAxisMin;
    const yAxisLabels = [];
    for (let i = yAxisMax; i >= yAxisMin; i -= 5) {
      yAxisLabels.push(i);
    }

    return { weightDataForChart, yAxisLabels, yAxisMin, yAxisMax, yAxisRange };
  };

  const chartData = prepareWeightDataForChart();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Wrapper */}
      <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header */}
        <header className="px-4 pt-8 pb-4 bg-white border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button 
                onClick={onBack}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-primary text-xl"></i>
              </button>
              <div className="flex items-center space-x-2">
                <Logo size="small" showText={false} />
                <span className="font-bold text-primary text-lg">Detail Sapi</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-24 bg-gray-50">
          {/* Main Cow Card */}
          <section className="px-4 pt-6">
            <div className="bg-secondary rounded-2xl shadow-lg overflow-hidden">
              {/* Cow ID Badge */}
              <div className="bg-primary p-4 text-center">
                <div className="bg-accent/20 rounded-lg px-4 py-2 inline-block">
                  <span className="text-white font-bold text-xl">
                    {loading ? 'Memuat...' : (produkDetail?.id || cowData.id || '-')}
                  </span>
                  <p className="text-white/80 text-sm mt-1">
                    {loading ? 'Memuat...' : (produkDetail?.nama || produkDetail?.name || cowData.breed || 'Produk')}
                  </p>
                </div>
              </div>

              {/* Cow Image */}
              <div className="relative h-64 bg-gradient-to-b from-accent/20 to-secondary">
                <img 
                  src={loading ? '/sapi1.jpg' : getProdukImageUrl()} 
                  alt={`Sapi ${produkDetail?.id || cowData.id}`} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/sapi1.jpg';
                  }}
                />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-black/50 backdrop-blur-sm rounded-lg p-3">
                    <div className="flex items-center justify-between text-white">
                      <div>
                        <p className="text-sm font-semibold">#IngatQurban</p>
                        <p className="text-xs opacity-80">#IngatPakMoo</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs opacity-80">pakMoo</p>
                        <p className="text-xs opacity-80">
                          {loading ? 'Memuat...' : (produkDetail?.kandang_nama || 'HUSA SENTOSA FARM')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Branding Section */}
              <div className="bg-white p-4">
                <div className="flex items-center justify-center space-x-4">
                  <div className="text-center">
                    <div className="bg-secondary rounded-lg p-2 mb-2">
                      <i className="fa-solid fa-leaf text-accent text-lg"></i>
                    </div>
                    <p className="text-primary font-semibold text-sm">
                      {loading ? 'Memuat...' : (produkDetail?.kandang_nama || 'NUSA SENTOSA FARM')}
                    </p>
                    <p className="text-secondary text-xs">pakMoo</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Data Section */}
          <section className="px-4 mt-6">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-accent p-3">
                <h3 className="text-primary font-bold text-center">Data</h3>
              </div>
              <div className="p-4 space-y-3">
                {loading ? (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                    </div>
                    <div className="flex justify-between">
                      <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Kode Unik Produk</span>
                      <span className="text-primary font-semibold text-sm">
                        {produkDetail?.id || cowData.id} {new Date().getFullYear()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Nama</span>
                      <span className="text-primary font-semibold text-sm">
                        {produkDetail?.nama || produkDetail?.name || cowData.name || 'Belum Ada'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Jenis Produk</span>
                      <span className="text-primary font-semibold text-sm">
                        {produkDetail?.jenis_produk?.nama || produkDetail?.jenis_produk_detail?.nama || cowData.productType || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Kategori</span>
                      <span className="text-primary font-semibold text-sm">
                        {produkDetail?.kategori_produk?.nama || produkDetail?.kategori || cowData.category || 'Standard'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Target Bobot</span>
                      <span className="text-primary font-semibold text-sm">
                        {produkDetail?.target_bobot ? `${produkDetail.target_bobot} Kg` : (cowData.targetWeight || '-')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Status</span>
                      <span className={`font-semibold text-sm ${produkDetail?.is_sold ? 'text-red-600' : 'text-green-600'}`}>
                        {produkDetail?.is_sold ? 'Terjual' : 'Tersedia'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* Deskripsi Section */}
          {(loading || produkDetail?.deskripsi) && (
            <section className="px-4 mt-4">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="bg-accent p-3">
                  <h3 className="text-primary font-bold text-center">Deskripsi</h3>
                </div>
                <div className="p-4">
                  {loading ? (
                    <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto animate-pulse"></div>
                  ) : (
                    <p className="text-gray-700 text-sm">
                      {produkDetail?.deskripsi || produkDetail?.description || '-'}
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Grafik Timbangan Section */}
          <section className="px-4 mt-4">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-accent p-3">
                <h3 className="text-primary font-bold text-center">Grafik Timbangan</h3>
              </div>
              <div className="p-4">
                {loading ? (
                  <div className="bg-gray-100 rounded-lg p-4 h-64 animate-pulse"></div>
                ) : !chartData.weightDataForChart || chartData.weightDataForChart.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <i className="fa-solid fa-chart-line text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500 text-sm">Belum ada data timbangan</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-4 h-64 relative border border-gray-100">
                    {/* Y-axis labels */}
                    <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-600 pr-2 w-8">
                      {chartData.yAxisLabels.map((weight) => (
                        <span key={weight} className="text-center">{weight}</span>
                      ))}
                    </div>
                    
                    {/* Chart area */}
                    <div className="ml-10 h-full relative">
                      {/* Grid lines */}
                      <div className="absolute inset-0 flex flex-col justify-between">
                        {chartData.yAxisLabels.map((_, i) => (
                          <div key={i} className="border-t border-gray-200"></div>
                        ))}
                      </div>
                      
                      {/* Data points and line */}
                      <div className="absolute inset-0 flex items-center justify-between px-4">
                        {chartData.weightDataForChart.map((data, index) => (
                          <div key={index} className="relative flex flex-col items-center flex-1">
                            {/* Data point */}
                            <div 
                              className="absolute bg-accent rounded-full w-4 h-4 border-2 border-white shadow-sm z-10"
                              style={{ 
                                bottom: chartData.yAxisRange > 0 
                                  ? `${((data.weight - chartData.yAxisMin) / chartData.yAxisRange) * 100}%` 
                                  : '50%'
                              }}
                            ></div>
                            
                            {/* Month labels */}
                            <div className="absolute -bottom-8 text-xs text-gray-600 font-medium text-center w-full truncate">
                              {data.month}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Line connecting points */}
                      {chartData.weightDataForChart.length > 1 && (
                        <svg className="absolute inset-0 w-full h-full pointer-events-none">
                          <polyline
                            points={chartData.weightDataForChart.map((data, index) => {
                              const x = chartData.weightDataForChart.length > 1 
                                ? (index / (chartData.weightDataForChart.length - 1)) * 100 
                                : 50;
                              const y = chartData.yAxisRange > 0 
                                ? 100 - ((data.weight - chartData.yAxisMin) / chartData.yAxisRange) * 100
                                : 50;
                              return `${x}%,${y}%`;
                            }).join(' ')}
                            fill="none"
                            stroke="#f6bf3f"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Data Timbangan Section */}
          <section className="px-4 mt-4">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-accent p-3">
                <h3 className="text-primary font-bold text-center">Data Timbangan</h3>
              </div>
              <div className="p-4">
                {loading ? (
                  <div className="space-y-3">
                    <div className="h-12 bg-gray-100 rounded animate-pulse"></div>
                    <div className="h-12 bg-gray-100 rounded animate-pulse"></div>
                  </div>
                ) : timbanganList && timbanganList.length > 0 ? (
                  <div className="space-y-3">
                    {timbanganList.map((timbangan, index) => {
                      const bobot = timbangan.bobot || timbangan.weight || 0;
                      const bobotNumber = typeof bobot === 'number' ? bobot : parseFloat(bobot) || 0;
                      const tanggal = timbangan.created_at || timbangan.tanggal || '';
                      const bulan = tanggal ? formatBulanTahun(tanggal) : `Data ${index + 1}`;
                      const produkId = produkDetail?.id || cowData.id || '-';
                      const tahun = tanggal ? new Date(tanggal).getFullYear() : new Date().getFullYear();
                      
                      return (
                        <div key={timbangan.id || index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                          <div>
                            <p className="text-gray-600 text-sm">{bulan}</p>
                            <p className="text-primary font-semibold text-sm">{produkId} {tahun}</p>
                          </div>
                          <span className="text-primary font-bold">{bobotNumber} Kg</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <i className="fa-solid fa-weight text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500 text-sm">Belum ada data timbangan</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Recording Ternak Section */}
          <section className="px-4 mt-4">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-accent p-3">
                <h3 className="text-primary font-bold text-center">Recording Ternak</h3>
              </div>
              <div className="p-4 space-y-4">
                {loading ? (
                  <div className="space-y-4">
                    <div className="h-16 bg-gray-100 rounded animate-pulse"></div>
                    <div className="h-16 bg-gray-100 rounded animate-pulse"></div>
                  </div>
                ) : (
                  <>
                    {/* Kondisi Saat Ini */}
                    <div>
                      <h4 className="text-gray-700 font-semibold mb-2">Kondisi Saat ini</h4>
                      <div className="bg-green-100 rounded-xl p-4 flex items-center space-x-3">
                        <div className="bg-green-500 rounded-full w-8 h-8 flex items-center justify-center">
                          <i className="fa-solid fa-heart text-white text-sm"></i>
                        </div>
                        <span className="text-green-700 font-semibold">Sehat</span>
                      </div>
                    </div>

                    {/* Vaksin */}
                    <div>
                      <h4 className="text-gray-700 font-semibold mb-2">Vaksin</h4>
                      {vaksinList && vaksinList.length > 0 ? (
                        <div className="space-y-2">
                          {vaksinList.map((vaksin, index) => {
                            const idVaksin = vaksin.id || `VKS${index + 1}`;
                            const tanggal = vaksin.created_at || vaksin.tanggal || '';
                            const tanggalFormatted = tanggal ? formatDate(tanggal) : '-';
                            const jenisVaksin = vaksin.jenis_vaksin || 'Vaksin';
                            
                            return (
                              <div key={vaksin.id || index} className="bg-white rounded-xl p-3 flex items-center space-x-3 border border-gray-200">
                                <div className="bg-green-100 rounded-lg p-2">
                                  <i className="fa-solid fa-syringe text-green-600"></i>
                                </div>
                                <div className="flex-1">
                                  <p className="text-gray-600 text-sm">{jenisVaksin}</p>
                                  <p className="text-primary font-semibold text-sm">{idVaksin}</p>
                                  <p className="text-gray-500 text-xs">{tanggalFormatted}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                          <i className="fa-solid fa-syringe text-2xl text-gray-300 mb-2"></i>
                          <p className="text-gray-500 text-xs">Belum ada data vaksin</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        </main>

        {/* Bottom Navigation */}
        <footer className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-white pt-2">
          <div className="bg-primary mx-4 mb-4 rounded-2xl shadow-lg">
            <div className="flex justify-around items-center h-20 text-white">
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'home' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('home');
                  onBack();
                }}
              >
                <i className="fa-solid fa-house text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Beranda</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'savings' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('savings');
                  onBack();
                }}
              >
                <i className="fa-solid fa-piggy-bank text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Tabungan</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'products' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('products');
                  onBack();
                }}
              >
                <i className="fa-solid fa-box-archive text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Produk</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'investment' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('investment')}
              >
                <i className="fa-solid fa-arrow-trend-up text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Investasi</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'account' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('account');
                  onBack();
                }}
              >
                <i className="fa-solid fa-user-circle text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Akun</span>
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CowDetailPage;
